import { useCallback, useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where, runTransaction, serverTimestamp, documentId } from '../../lib/auditedFirestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { canAccessTracker, canManageTracker, isCalendarDate, trackerDayPath, trackerProgressPath, validateDayPlan } from './trackerModel';

const EMPTY_DAY = { day: null, progress: {}, loading: false, error: '' };
const accessMessage = 'You do not have access to this subject’s tracker.';
export function useTrackerPapers() {
    const { user, userData } = useAuth();
    const [state, setState] = useState({ papers: [], loading: true, error: '' });
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        let active = true,failed=false;
        const ids = [...new Set(userData?.assignedPapers || [])].filter(id => canAccessTracker(userData, id));
        const all = userData?.role === 'superadmin' || (userData?.role === 'admin' && !userData.allowedFeatures && !userData.assignedPapers);
        if (!user?.uid || (!all && !ids.length)) { setState({ papers: [], loading: false, error: '' }); return; }
        setState({ papers: [], loading: true, error: '' });
        // Subscribe to exact assigned IDs. Rules never need to grant a student a full paper listing.
        const chunks = all ? [null] : Array.from({ length: Math.ceil(ids.length / 30) }, (_, i) => ids.slice(i * 30, i * 30 + 30));
        const results = new Map();
        const stops = chunks.map((chunk, index) => onSnapshot(chunk ? query(collection(db, 'papers'), where(documentId(), 'in', chunk)) : collection(db, 'papers'), snapshot => {
            if (!active || failed) return;
            results.set(index, snapshot.docs.map(d => ({ ...d.data(), id: d.id })).filter(paper => canAccessTracker(userData, paper.id)));
            setState({ papers: [...results.values()].flat().sort((a,b) => (a.name || '').localeCompare(b.name || '')), loading: results.size < chunks.length, error: '' });
        }, () => { if (active) {failed=true;setState({ papers: [], loading: false, error: 'Could not load your subjects. Check your connection and access.' });} }));
        return () => { active = false; stops.forEach(stop => stop()); };
    }, [user?.uid, userData, revision]);
    return { ...state, reload: () => setRevision(n => n + 1) };
}

export function useTrackerMonth(paperId, month) {
    const { user, userData } = useAuth();
    const [state, setState] = useState({ key:'',days: {}, loading: true, error: '' });
    const [revision, setRevision] = useState(0);
    const allowed = canAccessTracker(userData, paperId),key=`${paperId}:${month}:${user?.uid}:${allowed}`;
    useEffect(() => {
        if (!user?.uid || !allowed) { setState({key,days:{},loading:false,error:accessMessage}); return; }
        let active=true;setState({key,days:{},loading:true,error:''});
        const stop=onSnapshot(query(collection(db, 'papers', paperId, 'iqTrackerDays'), where('date', '>=', `${month}-01`), where('date', '<=', `${month}-31`)), snapshot => {
            if(active)setState({key,days:Object.fromEntries(snapshot.docs.map(d => [d.id, {...d.data(),id:d.id}])),loading:false,error:''});
        }, () => {if(active)setState({key,days:{},loading:false,error:'Could not load the calendar. Check your connection or ask your administrator to check tracker access.'});});
        return()=>{active=false;stop();};
    }, [paperId, month, user?.uid, allowed, revision,key]);
    return {...(state.key===key?state:{days:{},loading:true,error:''}),reload:()=>setRevision(n=>n+1)};
}

export function useTrackerDay(paperId, date) {
    const { user, userData } = useAuth();
    const [state, setState] = useState(EMPTY_DAY);
    const [revision, setRevision] = useState(0);
    const allowed = canAccessTracker(userData, paperId);
    useEffect(() => {
        if (!date) { setState(EMPTY_DAY); return; }
        const key = `${paperId}:${date}:${user?.uid}`;
        if (!isCalendarDate(date) || !allowed || !user?.uid) { setState({ ...EMPTY_DAY, key, error: !isCalendarDate(date) ? 'This tracker date is invalid.' : accessMessage }); return; }
        let active = true;
        setState({ ...EMPTY_DAY, key, loading: true });
        // Await both initial snapshots, so resume never uses an empty progress placeholder.
        let dayLoaded = false, progressLoaded = false;
        let day = null, progress = {};
        const publish = () => { if (active) setState({ day, progress, key, loading: !dayLoaded || !progressLoaded, error: '' }); };
        const fail = () => { if (active) { active = false; setState({ ...EMPTY_DAY, key, error: 'Could not load this daily plan. Check your connection or ask your administrator to enable tracker access.' }); } };
        const stopDay = onSnapshot(doc(db, ...trackerDayPath(paperId, date)), snap => { day = snap.exists() ? { ...snap.data(), id: snap.id } : null; dayLoaded = true; publish(); }, fail);
        const stopProgress = onSnapshot(doc(db, ...trackerProgressPath(paperId, date, user.uid)), snap => { progress = snap.exists() ? snap.data() : {}; progressLoaded = true; publish(); }, fail);
        return () => { active = false; stopDay(); stopProgress(); };
    }, [paperId, date, user?.uid, allowed, revision]);
    const current = !date ? EMPTY_DAY : state.key === `${paperId}:${date}:${user?.uid}` ? state : { ...EMPTY_DAY, loading: true };
    return { ...current, reload: useCallback(() => setRevision(n => n + 1), []) };
}

export async function saveDayPlan({ paperId, date, title, instructions, questionIds, expectedRevision, userId, profile, questions }) {
    if (!userId || !canManageTracker(profile, paperId)) throw new Error('You do not have permission to manage this tracker.');
    if (!Number.isInteger(expectedRevision) || expectedRevision < 0) throw new Error('Reload this day before editing its plan.');
    const plan = { date, paperId, title: title.trim(), instructions: instructions.trim(), questionIds };
    validateDayPlan(plan, questions);
    return runTransaction(db, async transaction => {
        const reference = doc(db, ...trackerDayPath(paperId, date));
        const existing = await transaction.get(reference);
        const previous = existing.exists() ? existing.data() : {};
        if ((previous.revision || 0) !== expectedRevision) throw new Error('Another admin updated this day. Close the editor and reopen it to see the latest plan.');
        const snapshots = await Promise.all(questionIds.map(id => transaction.get(doc(db, 'practiceQuestions', id))));
        if (snapshots.some(snap => !snap.exists() || snap.data().paperId !== paperId || snap.data().status === 'draft')) throw new Error('A selected question has been removed or unpublished. Reload the tracker and select it again.');
        transaction.set(reference, { ...plan, revision: expectedRevision + 1, createdBy: previous.createdBy || userId, updatedBy: userId, updatedAt: serverTimestamp() });
    });
}

export async function clearDayPlan(paperId, date, expectedRevision, userId, profile) {
    if (!isCalendarDate(date) || !canManageTracker(profile, paperId) || !userId) throw new Error('You do not have permission to clear this day.');
    return runTransaction(db, async transaction => {
        const reference = doc(db, ...trackerDayPath(paperId, date));
        const snap = await transaction.get(reference);
        if (!snap.exists() || (snap.data().revision || 0) !== expectedRevision) throw new Error('This plan changed. Reload it before clearing the day.');
        // Retain history and revision to prevent an older editor overwriting a newer plan.
        transaction.update(reference, { questionIds: [], revision: expectedRevision + 1, updatedBy: userId, updatedAt: serverTimestamp() });
    });
}

export async function removeDayQuestion({ paperId, date, questionId, expectedRevision, userId, profile }) {
    if (!isCalendarDate(date) || !userId || !canManageTracker(profile, paperId)) throw new Error('You do not have permission to remove questions from this day.');
    if (typeof questionId !== 'string' || !questionId || !Number.isInteger(expectedRevision) || expectedRevision < 0) throw new Error('Reload this day before removing a question.');
    return runTransaction(db, async transaction => {
        const reference = doc(db, ...trackerDayPath(paperId, date));
        const snap = await transaction.get(reference);
        if (!snap.exists() || (snap.data().revision || 0) !== expectedRevision) throw new Error('This daily plan changed. Review the latest questions and try removing it again.');
        const ids = snap.data().questionIds;
        if (!Array.isArray(ids) || !ids.includes(questionId)) throw new Error('This question is no longer assigned to this date.');
        // Updating only the assignment also handles the last question and unavailable IQ records.
        transaction.update(reference, { questionIds: ids.filter(id => id !== questionId), revision: expectedRevision + 1, updatedBy: userId, updatedAt: serverTimestamp() });
    });
}

export function useTrackerPaperProgress(paperId) {
    const { user, userData } = useAuth();
    const allowed = canAccessTracker(userData, paperId);
    const key = `${paperId}:${user?.uid}`;
    const [state, setState] = useState({ key: '', progress: {}, loading: true, error: '' });
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        if (!user?.uid || !allowed) { setState({ key, progress: {}, loading: false, error: accessMessage }); return; }
        let active = true;
        setState({ key, progress: {}, loading: true, error: '' });
        const stop = onSnapshot(doc(db, 'iqProgress', `${user.uid}_${paperId}`), snapshot => {
            if (active) setState({ key, progress: snapshot.exists() ? snapshot.data() : {}, loading: false, error: '' });
        }, () => {
            if (active) setState({ key, progress: {}, loading: false, error: 'Your paper progress could not be loaded. Check your connection and retry.' });
        });
        return () => { active = false; stop(); };
    }, [paperId, user?.uid, allowed, key, revision]);
    const current = state.key === key ? state : { progress: {}, loading: true, error: '' };
    return { ...current, reload: () => setRevision(n => n + 1) };
}
